import React, { useEffect } from "react";
import Footer from "../components/Footer/Footer";
import Navbar from "../components/Navigation/Navbar";

import { componentBuilder } from "../helpers/componentsHelper";
import SeoHelmet from "../components/Seo/SeoHelmet";
import { componentsOutsideBlocks } from "../helpers/componentsOutsideBlocks";
import { LocationContext } from "../helpers/locationContext";
import HeroPaidCalculator from "../components/HeroPaidCalculator/HeroPaidCalculator";
import AccordionPaid from "../components/AccordionPaid/AccordionPaid";
import {
  PaidStyles,
  PromoBar,
  FloatingFab,
  TrustBar,
  SectionCTA,
  TrustpilotReviews,
  PressLogos,
  MobilePhoneBlock,
  FinalCTA,
} from "../components/PaidLPSections/PaidLPSections";

// Disabled ABTest temporaly
// import RedirectsHelper from "../helpers/redirectsHelper";

// Paid-search LPs that use the custom brand-blue HeroPaidCalculator and suppress
// the redundant offer banners (the offer now lives in the hero pill). Scoped to
// the template only — shared block components stay untouched and every other
// page renders exactly as before. Trailing-slash / case-insensitive match.
const PAID_HERO_PAGES = ["/calcular-seguro-vida-axa/"];
// Block __typename comes through WITHOUT the "Strapi_" prefix at runtime
// (page-data.json) but WITH it during SSR/GraphQL — same dual-form gotcha that
// componentsOutsideBlocks handles. Strip the prefix before comparing.
const HERO_CALC_TYPE = "ComponentComponentHeroCalculatorImage";
const ACCORDION_TYPE = "ComponentComponentAccordion";
const ICONCARDS_TYPE = "ComponentComponentIconCards"; // "¿Por qué elegir…?" benefits
const COMPARATIVE_TYPE = "ComponentComponentComparativeTable"; // "AXA vs. otras"
const ICONSROW_TYPE = "ComponentComponentIconsRow"; // press logos band

// Match a Strapi BackgroundColor enum to the hex the section actually renders,
// so the appended SectionCTA strip blends seamlessly with the block above it.
const SECTION_BG: Record<string, string> = {
  Blue_Gray: "#F5F7FA",
  Light_Blue: "#ECF0FF",
  White: "#FFFFFF",
};
const bgFromBlock = (blockData: any) =>
  SECTION_BG[blockData?.BackgroundColor?.BackgroundColor || "White"] || "#FFFFFF";
// Blocks dropped on the paid LP: offer banners (offer now lives in the hero
// pill), the explainer animated-lines and the old promo-banner (replaced by the
// closing FinalCTA). All other blocks render normally.
const SUPPRESS_ON_PAID = new Set([
  "ComponentComponentBannerTitleImage",
  "ComponentComponentAnimatedLines",
  "ComponentComponentPromoBanner",
]);
const stripTypename = (t?: string) => (t ?? "").replace(/^Strapi_/, "");
const normalizeUrl = (url?: string) => (url ?? "").replace(/\/+$/, "").toLowerCase();

export default (data: any) => {
  const pageData = data.pageContext?.strapiData?.page || data.pageContext?.strapiData;

  if (!pageData) {
    throw new Error(
      `masterTemplate: received null strapiData for ${data.path ?? "unknown path"}`
    );
  }

  const navbarComponent = pageData.Navbar;
  const pageComponents = pageData.Blocks;
  const seoComponent = pageData.SEO;
  const footerComponent = pageData.Footer;
  const blocks = componentBuilder(pageComponents, pageData.locale);
  // const { ManageRedirects } = RedirectsHelper();

  // useEffect(() => {
  //   ManageRedirects();
  // }, []);

  const isPaidHeroLP = PAID_HERO_PAGES.map(normalizeUrl).includes(
    normalizeUrl(pageData.CustomUrl)
  );

  // On paid LPs only: rebuild the page from the brief while reusing the existing
  // block data and the SAME calculator form (untouched). Hero -> brand-blue
  // HeroPaidCalculator (+ TrustBar right after); accordion -> AccordionPaid (FAQ
  // with +/×, 2 cols, a11y); offer banners / animated-lines / promo-banner
  // dropped. The closing FinalCTA + mobile phone block are appended after the
  // loop. Every other page renders the original element unchanged.
  // Lazy-loaded blocks (React.lazy in componentsHelper, e.g. promo-banner,
  // animated-lines) are wrapped in <LazyWrap>, so their data sits one level
  // deeper at props.children.props.data — not props.data. Read both so type
  // detection works for lazy AND direct blocks. Returns {type, blockData}.
  const getBlock = (block: any) => {
    const direct = block?.props?.data;
    const child = block?.props?.children?.props?.data;
    const blockData = direct ?? child;
    return { type: stripTypename(blockData?.__typename), blockData };
  };

  const renderForMain = (block: any) => {
    if (!isPaidHeroLP) return block;
    const { type, blockData } = getBlock(block);
    if (type === HERO_CALC_TYPE) {
      return (
        <React.Fragment key={block.key}>
          <HeroPaidCalculator data={blockData} />
          <TrustBar />
        </React.Fragment>
      );
    }
    if (type === ICONCARDS_TYPE) {
      // Benefits — keep the CMS-driven block, append the brief's CTA below it.
      return (
        <React.Fragment key={block.key}>
          {block}
          <SectionCTA
            label="Calcular seguro de vida"
            location="benefits"
            bg={bgFromBlock(blockData)}
          />
        </React.Fragment>
      );
    }
    if (type === COMPARATIVE_TYPE) {
      // Comparison table — keep the CMS block, append the brief's CTA below it.
      return (
        <React.Fragment key={block.key}>
          {block}
          <SectionCTA
            label="Quiero mi precio"
            location="comparison"
            bg={bgFromBlock(blockData)}
          />
        </React.Fragment>
      );
    }
    if (type === ACCORDION_TYPE) {
      // Inject the Trustpilot carousel right before the FAQ (brief order 7→8).
      return (
        <React.Fragment key={block.key}>
          <TrustpilotReviews />
          <AccordionPaid data={blockData} />
        </React.Fragment>
      );
    }
    if (type === ICONSROW_TYPE) {
      // Press logos — same images, restyled "Recomendado por:" band.
      return <PressLogos data={blockData} key={block.key} />;
    }
    if (SUPPRESS_ON_PAID.has(type)) return null;
    return block;
  };

  return (
    <LocationContext.Provider value={data.location || {}}>
      <SeoHelmet data={seoComponent} locale={pageData.locale} customUrl={pageData.CustomUrl} title={pageData.Title} pathname={data.location?.pathname}/>
      {isPaidHeroLP && (
        <>
          <PaidStyles />
          <PromoBar />
        </>
      )}
      {navbarComponent && <Navbar data={navbarComponent} locale={pageData.locale} />}

      <main className="page-content">
      {blocks?.map((block, id) => {
        const type = block?.props?.data?.__typename;
        if (componentsOutsideBlocks.includes(type)) return null;
        const rendered = renderForMain(block);
        if (rendered === null) return null;
        return <div key={id}>{rendered}</div>;
      })}
      {isPaidHeroLP && (
        <>
          <MobilePhoneBlock />
          <FinalCTA />
          <FloatingFab />
        </>
      )}
      </main>

      {footerComponent && <Footer data={footerComponent} />}
      {blocks?.map((block, id) => {
        const type = block?.props?.data?.__typename;
        if (componentsOutsideBlocks.includes(type))return (
          <React.Fragment key={id}>
            {block}
          </React.Fragment>
        );
        return null;
      })}
    </LocationContext.Provider>
  );
};
